import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Accordion from '../../../shared/Accordion';
import ExternalLink from '../../../shared/ExternalLink';
import { ActiveStatusPill, CategoryPill, Pill } from '../../../shared/Pill';
import { usePermissions } from '../../../shared/PermissionsContext';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import BrandForm from '../../inventory/Brands/BrandForm';

// An Organisation page's Brands card (#309): the Brands this Organisation
// owns, and Add Brand with the Owner already set to it. Shown on every
// Organisation, outside the Entity Cards grid — any Organisation can own a
// Brand, whatever Relationship Types it holds. Editing a Brand stays on the
// Brands screen; each name links there (?edit=<id>). Company Information shows the same card for the Brands the
// business itself owns (#430), with its own origin.
function OwnedBrandsCard({ entity, origin = { label: entity.name, path: `/commercial-entities/${entity.id}` } }) {
  const { accessFor } = usePermissions();
  const navigate = useNavigate();
  const canCreate = accessFor('inventory-brands').create;
  const canUpdate = accessFor('inventory-brands').update;
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get('/api/brands', { params: { status: 'all', owner_id: entity.id } });
      setBrands(res.data.data || []);
    } catch {
      setBrands([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [entity.id]);

  const handleSave = async (data) => {
    setSaving(true);
    try {
      await axios.post('/api/brands', data);
      setAdding(false);
      dismissError();
      await load();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSaving(false);
  };

  return (
    <Accordion
      title={`Brands (${brands.filter((b) => b.is_active).length})`}
      open={open}
      onToggle={() => setOpen((o) => !o)}
      menuItems={canCreate ? [
        { key: 'add-brand', label: 'Add Brand', disabled: adding, onClick: () => { setAdding(true); setOpen(true); } },
      ] : undefined}
    >
      {adding && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 14, borderRadius: 8, marginBottom: 10 }}>
          <BrandForm
            initialOwner={entity}
            origin={origin}
            onSave={handleSave}
            onCancel={() => setAdding(false)}
            loading={saving}
          />
        </div>
      )}
      {brands.length ? brands.map((b) => (
        <div key={b.id} style={{ padding: '6px 0', borderBottom: '1px solid #eef1f5', fontSize: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {canUpdate ? (
              <button
                type="button"
                onClick={() => navigate(`/inventory-brands?edit=${b.id}`)}
                title="Edit this Brand on the Brands list"
                style={{ background: 'none', border: 'none', padding: 0, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
              >
                {b.name}
              </button>
            ) : <span style={{ fontWeight: 600 }}>{b.name}</span>}
            {b.brand_type && <CategoryPill value={b.brand_type.name} />}
            {b.show_on_website && <Pill color="#1b6e3a" background="#e3f4e8" title={`Shown on the website at /brands/${b.web_slug}`}>On website</Pill>}
            {!b.is_active && <ActiveStatusPill active={false} />}
            {b.url && <span style={{ marginLeft: 'auto', fontSize: 13, minWidth: 0 }}><ExternalLink href={b.url} maxWidth={180} /></span>}
          </div>
          {b.description && <div style={{ marginTop: 4, fontSize: 13, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={b.description}>{b.description}</div>}
        </div>
      )) : !adding && <div style={{ fontSize: 13, color: '#888' }}>No Brands yet</div>}
    </Accordion>
  );
}

export default OwnedBrandsCard;
